import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule } from '@angular/forms';
import { Params, Router, RouterModule } from '@angular/router';
import { ProductSearchFilterOptions } from '@app/akita/api/products/models/product-search.filter-options.model';
import { ProductVariant } from '@app/akita/api/products/models/product-variant.model';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { AkitaProductsService } from '@app/akita/api/products/state/products.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { ApiError } from '@app/shared/models/api/api-error.model';
import { TranslocoModule } from '@ngneat/transloco';
import { Observable, of, Subscription, tap } from 'rxjs';
import { colorNameToHex } from '@app/akita/api/products/models/product.model';
import { TranslateColorPipe } from '@app/shared/pipes/translate-color.pipe';

@Component({
  selector: 'app-product-color-variants',
  templateUrl: './product-color-variants.template.html',
  styleUrls: ['./product-color-variants.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    CommonModule,
    TranslocoModule,
    FlexLayoutModule,
    RouterModule,
    TranslateColorPipe,
  ],
})

/**
 * PRODUCT COLOR VARIANTS
 * we do use color variant from Products API
 * in order to generate the fragment for the variant
 * we get the variants now from product/options?model
 *
 * if we do not want to use the link ( at the search page)
 * we flag disableLink = true
 *
 * we keep the code for eventual changes back
 ***/
export class ProductColorVariantsComponent implements OnChanges, OnDestroy {
  @Input() public id?: string | null;
  @Input() public model?: string | null;
  @Input() public displayMode?: string = 'thumb';
  @Input() public showLabel?: boolean | null;
  @Input() public switchMode?: boolean | null = false;

  @Output() public readonly switchProductID: EventEmitter<string>;
  public readonly biggerThan$: Observable<ScreenSizeInfoState>;

  public productOptions$: Observable<ProductSearchFilterOptions | null>;

  public variants: Array<ProductVariant>;
  public colorVariants$: Observable<Array<ProductVariant>>;
  public findingColorVariants$: Observable<boolean>;
  public colorVariantsError$: Observable<ApiError | null>;
  public readonly queryParamsToKeep$: Observable<Params>;

  private readonly subscriptions: Subscription;

  constructor(
    private readonly zone: NgZone,
    private readonly router: Router,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaProductsService: AkitaProductsService,
    private readonly akitaScreenQuery: AkitaScreenQuery
  ) {
    this.switchProductID = new EventEmitter();
    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();
    this.subscriptions = new Subscription();
    this.colorVariants$ = of([]);

    this.variants = new Array(0);
    this.findingColorVariants$ = of(false);
    this.colorVariantsError$ = of(null);
    this.queryParamsToKeep$ = this.akitaRouterQuery.selectQueryParamsToKeep();

    this.productOptions$ = this.akitaProductsService.getAvailableProductOptions(
      this.model || ''
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.id || changes.model) {
      this.refresh();
    }
  }

  private refresh(): void {
    this.colorVariants$ = this.akitaProductsQuery
      .selectProductVariants(this.model)
      .pipe(tap((variants: Array<ProductVariant>) => (this.variants = [...variants])));

    this.findingColorVariants$ = this.akitaProductsQuery.selectIsSearchingProductVariants(
      this.id
    );

    this.colorVariantsError$ =
      this.akitaProductsQuery.selectErrorSearchingProductVariants(this.id);

    this.productOptions$ = this.akitaProductsService.getAvailableProductOptions(
      this.model || ''
    );

    this.changeDetector.markForCheck();

    if (this.akitaRouterQuery.isBrowser) {
      this.zone.runOutsideAngular(this.fetchVariantsInBG.bind(this));
    } else {
      this.fetchVariantsInBG();
      this.fetchOptionsInBG();
    }
  }

  private fetchVariantsInBG(): void {
    if (this.akitaRouterQuery.isBrowser) {
      this.zone.runOutsideAngular(() => {
        this.subscriptions.add(
          this.akitaProductsService.getProductVariantsByModelAsync(this.model)
        );
      });
    } else {
      this.subscriptions.add(
        this.akitaProductsService.getProductVariantsByModelAsync(this.model)
      );
    }
  }

  private fetchOptionsInBG(): void {
    if (this.akitaRouterQuery.isBrowser) {
      this.zone.runOutsideAngular(() => {
        this.subscriptions.add(
          this.akitaProductsService.getAvailableProductOptionsAsync(this.model || '')
        );
      });
    } else {
      this.subscriptions.add(
        this.akitaProductsService.getAvailableProductOptionsAsync(this.model || '')
      );
    }
  }

  public isVariantAvailable(colorCode: string): boolean {
    const currentVariant = this.akitaProductsQuery.getProductVariant(this.id, this.model);

    return Boolean(
      this.akitaProductsQuery.getMatchingColorVariant(
        this.model,
        currentVariant,
        colorCode
      )
    );
  }

  public getVariantFragment(colorCode: string | null): Array<string> {
    const currentVariant = this.akitaProductsQuery.getProductVariant(this.id, this.model);
    const aux = this.akitaProductsQuery.getMatchingColorVariant(
      this.model,
      currentVariant,
      colorCode
    );

    const out = this.akitaRouterQuery.getProductUrlFragment(
      aux?.id || '',
      aux?.title || ''
    );

    return out;
  }

  public isVariantSelected(colorCode: string): boolean {
    const currentVariant = this.akitaProductsQuery.getProductVariant(this.id, this.model);
    return currentVariant?.colorCode === colorCode;
  }

  public switchVariant(colorCode: string | null): void {
    const currentVariant = this.akitaProductsQuery.getProductVariant(this.id, this.model);
    const aux = this.akitaProductsQuery.getMatchingColorVariant(
      this.model,
      currentVariant,
      colorCode,
      true
    );
    if (this.switchMode) {
      this.switchProductID.emit(aux?.id);
    } else if (aux?.inStock) {
      const fragment = this.akitaRouterQuery.getProductUrlFragment(aux.id);

      this.subscriptions.add(
        this.akitaProductsService.getProduct(aux.id).subscribe({
          next: () => {
            this.router
              .navigate(fragment, { queryParamsHandling: 'merge' })
              .catch((error: unknown) => {
                console.warn(error);
              });
          },
          error: () => {},
        })
      );
    }
  }

  public trackByProduct(index: number, value?: any | null): string {
    return `${value?.id || index}`;
  }

  public getColor(color?: string | null) {
    return colorNameToHex(color);
  }

  public getTransLabel(color?: string | null): string {
    if (color) {
      return `label_phone_color_${color}`.toLowerCase();
    }
    return '';
  }

  public ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
